import { useState, useEffect } from "react";
import { Button, HeaderMark } from "@repo/o37-ui";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";


export default function ResetPassword() {
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Get token from URL query parameter
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError("Invalid reset link. Please request a new password reset.");
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          window.location.href = "/";
        }, 3000);
      } else {
        setError(data.error || "Failed to reset password");
      }
    } catch (err) {
      setError("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-screen bg-white text-black flex flex-col">
        <HeaderMark />

        <section className="flex-1 flex items-center justify-center px-6 sm:px-12">
          <div className="w-full max-w-xl">
            <div className="rounded-2xl border border-neutral-200 p-10 text-center">
              <div className="text-4xl mb-4">✅</div>
              <h1 className="text-3xl font-semibold mb-3">Password Reset Successfully</h1>
              <p className="text-neutral-600 text-base mb-6">
                Your password has been reset. Redirecting to login...
              </p>
              <a
                href="/"
                className="inline-block border border-neutral-800 text-white bg-neutral-900 rounded-xl px-6 py-3 text-base font-medium hover:bg-neutral-800"
              >
                Go to Login
              </a>
            </div>
          </div>
        </section>

        <footer className="px-6 sm:px-12 mt-24 mb-10">
          <div className="max-w-5xl mx-auto flex items-center justify-between text-[12px] text-neutral-500">
            <div>By all means, accelerate.</div>
            <div>🏴</div>
          </div>
        </footer>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white text-black flex flex-col">
      <HeaderMark />

      <section className="flex-1 flex items-center justify-center px-6 sm:px-12">
        <div className="w-full max-w-xl">
          <div className="rounded-2xl border border-neutral-200 p-10">
            <h1 className="text-3xl font-semibold mb-3">Set New Password</h1>
            <p className="text-neutral-600 text-base mb-8">
              Enter your new password below
            </p>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <label className="flex flex-col gap-2">
                  <span className="text-sm text-neutral-600">New Password</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="border border-neutral-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-neutral-800"
                    placeholder="••••••••"
                    required
                    disabled={loading || !token}
                    minLength={6}
                  />
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-sm text-neutral-600">Confirm Password</span>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="border border-neutral-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-neutral-800"
                    placeholder="••••••••"
                    required
                    disabled={loading || !token}
                    minLength={6}
                  />
                </label>
              </div>

              {error && (
                <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  {error}
                </div>
              )}

              <div className="mt-6 flex justify-center">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading || !token || !password || !confirmPassword}
                  className="text-base py-3 h-auto w-auto px-8"
                  text={loading ? "Resetting..." : "Reset Password"}
                />
              </div>

              <div className="mt-4 text-center">
                <a
                  href="/"
                  className="text-sm text-neutral-600 hover:text-neutral-900 underline"
                >
                  Back to Login
                </a>
              </div>
            </form>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 sm:px-12 mt-24 mb-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between text-[12px] text-neutral-500">
          <div>By all means, accelerate.</div>
          <div>🏴</div>
        </div>
      </footer>
    </main>
  );
}

